import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { snakeCaseToSentenceCase } from "../../../utils/formatText";
import { resolveFilterOption } from "../../merchandising/components/tables/resolveFilterOption";
import { AD_STATES } from "../../../components/core/blocks/AdPlan/constants";

export const KEYWORD_MATCH_TYPE_OPTIONS = [
  { value: "phrase", label: ($) => $.advertising["Phrase"] },
  { value: "broad", label: ($) => $.advertising["Broad"] },
  { value: "exact", label: ($) => $.advertising["Exact"] },
  { value: "negativePhrase", label: ($) => $.advertising["Negative Phrase"] },
  { value: "negativeExact", label: ($) => $.advertising["Negative Exact"] },
];
export const TARGET_MATCH_TYPE_OPTIONS = [
  { value: "auto", label: ($) => $.advertising["Auto"] },
  { value: "product", label: ($) => $.advertising["Product"] },
  { value: "category", label: ($) => $.advertising["Category"] },
  { value: "dynamic", label: ($) => $.advertising["Dynamic"] },
  { value: "expression", label: ($) => $.advertising["Expression"] },
  { value: "audience", label: ($) => $.advertising["Audience"] },
];

export const SEARCH_TERM_REPORT_MATCH_TYPE_OPTIONS = [
  { value: "phrase", label: ($) => $.advertising["Phrase"] },
  { value: "broad", label: ($) => $.advertising["Broad"] },
  { value: "exact", label: ($) => $.advertising["Exact"] },
  { value: "targeting_expression_predefined", label: ($) => $.advertising["Auto"] },
  { value: "targeting_expression", label: ($) => $.advertising["Expression"] },
];

// Lookup by raw match_type value → i18n selector. Used to render the cell
// rather than humanizing the snake/camel-cased value with regex.
export const MATCH_TYPE_SELECTORS = Object.fromEntries(
  [...KEYWORD_MATCH_TYPE_OPTIONS, ...TARGET_MATCH_TYPE_OPTIONS, ...SEARCH_TERM_REPORT_MATCH_TYPE_OPTIONS].map((o) => [
    o.value,
    o.label,
  ]),
);

// Lookup by raw target state value → i18n selector. Mirrors TARGET_STATE_OPTIONS
// minus the "all" filter sentinel.
export const TARGET_STATE_SELECTORS = Object.fromEntries(
  [
    { value: "enabled", label: ($) => $.advertising["Enabled"] },
    { value: "paused", label: ($) => $.advertising["Paused"] },
  ].map((o) => [o.value, o.label]),
);

export const TARGET_STATE_OPTIONS = [
  {
    value: "all",
    label: ($) => $.advertising["All States"],
  },
  {
    value: "enabled",
    label: ($) => $.advertising["Enabled"],
  },
  {
    value: "paused",
    label: ($) => $.advertising["Paused"],
  },
];

export const AD_STATE_OPTIONS = [
  { value: "all", label: ($) => $.advertising["All States"] },
  { value: AD_STATES.ACTIVE.value, label: ($) => $.advertising["Active"] },
  { value: AD_STATES.PAUSE.value, label: ($) => $.advertising["Paused"] },
  { value: AD_STATES.ENDED.value, label: ($) => $.advertising["Ended"] },
];

export const BID_TREND_OPTIONS = [
  {
    value: "Increasing",
    label: ($) => $.advertising["Increasing"],
  },
  {
    value: "Decreasing",
    label: ($) => $.advertising["Decreasing"],
  },
  {
    value: "Stable",
    label: ($) => $.advertising["Stable"],
  },
];

export const WALMART_TARGET_SOURCE_OPTIONS = [
  {
    value: "all",
    label: ($) => $.advertising["All Sources"],
  },
  { value: "user", label: ($) => $.advertising["User"] },
  { value: "system", label: ($) => $.advertising["System"] },
  { value: "GV_DISCOVERY", label: ($) => $.advertising["Initial"] },
  { value: "harvested", label: ($) => $.advertising["Harvesting"] },
];

export const AMAZON_TARGET_SOURCE_OPTIONS = [
  {
    value: "all",
    label: ($) => $.advertising["All Sources"],
  },
  { value: "GV_DISCOVERY", label: ($) => $.advertising["Initial"] },
  { value: "harvested", label: ($) => $.advertising["Harvesting"] },
  { value: "harvested_v2", label: ($) => $.advertising["Harvesting V2"] },
];

const getManualBidFilterOptions = (tableId) => {
  return [
    {
      value: "withManualBids",
      label: `${tableId === "keyword" ? "Keyword Targets" : "Product Targets"} with Manual Bids`,
    },
    {
      value: "withSuppressedBudget",
      label: `${tableId === "keyword" ? "Keyword Targets" : "Product Targets"} with Constrained Bids`,
    },
    {
      value: "withScheduledActions",
      label: `${tableId === "keyword" ? "Keyword Targets" : "Product Targets"} with Scheduled Bid Boost`,
    },
    {
      value: "withBidRange",
      label: `${tableId === "keyword" ? "Keyword Targets" : "Product Targets"} with a Bid Range`,
    },
  ];
};

export const getTargetBidTypeFilter = (tableId) => {
  const BID_TYPE_OPTIONS = getManualBidFilterOptions(tableId);

  return {
    name: ($) => $.advertising["Bid Type"],
    id: "bid_type",
    format: "select",
    selectOptions: [
      {
        label: `All ${snakeCaseToSentenceCase(tableId)}s`,
        value: "all",
        clearable: false,
      },
      ...BID_TYPE_OPTIONS,
    ],
    override: true,
    defaultValue: "all",
    operator: "custom",
    filterFunction: (target, filterValue) => {
      if (filterValue !== "all") {
        if (filterValue === "withManualBids") {
          return target["manual_bid"] > 0;
        }
        if (filterValue === "withSuppressedBudget") {
          return target["budget_suppression"];
        }
        if (filterValue === "withScheduledActions") {
          return target["scheduled_actions"]?.length;
        }
        if (filterValue === "withBidRange") {
          return target["ceil_bid"] && target["floor_bid"];
        }
      }
      return true;
    },
  };
};

export const BID_PREVIEW_WARNING_FILTER = {
  name: ($) => $.advertising["Bid Preview"],
  id: "bid_warning",
  format: "select",
  selectOptions: [
    {
      label: ($) => $.advertising["All"],
      value: "all",
      clearable: false,
    },
    {
      label: ($) => $.advertising["Bids with warning"],
      value: "bids_with_warning",
      clearable: true,
    },
  ],
  override: true,
  defaultValue: "all",
  operator: "=",
  filterFunction: (target, filterValue) => {
    const highBid = target.preview_bid > target.target_bid * 1.5;
    const lowBid = target.preview_bid < target.target_bid * 0.5;
    if (filterValue === "bids_with_warning") {
      return lowBid || highBid;
    }
    return true;
  },
};

export const KEYWORD_MATCH_TYPE_FILTER = {
  name: ($) => $.advertising["Match Type"],
  id: "match_type",
  format: "select",
  selectOptions: [
    {
      value: "all",
      label: ($) => $.advertising["All Match Types"],
    },
    ...KEYWORD_MATCH_TYPE_OPTIONS,
  ],
  override: true,
  defaultValue: "all",
  operator: "custom",
  filterFunction: (keyword, filterValue) => {
    if (filterValue !== "all") {
      return keyword.match_type === filterValue;
    }
    return true;
  },
};

export const SEARCH_TERM_REPORT_MATCH_TYPE_FILTER = {
  name: ($) => $.advertising["Match Type"],
  id: "match_type",
  format: "select",
  selectOptions: [
    {
      value: "all",
      label: ($) => $.advertising["All Match Types"],
    },
    ...SEARCH_TERM_REPORT_MATCH_TYPE_OPTIONS,
  ],
  override: true,
  defaultValue: "all",
  operator: "custom",
  filterFunction: (keyword, filterValue) => {
    if (filterValue !== "all") {
      return keyword.match_type.toLowerCase() === filterValue.toLowerCase();
    }
    return true;
  },
};

export const TARGET_MATCH_TYPE_FILTER = {
  name: ($) => $.advertising["Match Type"],
  id: "match_type",
  format: "select",
  selectOptions: [
    {
      value: "all",
      label: ($) => $.advertising["All Match Types"],
    },
    ...TARGET_MATCH_TYPE_OPTIONS,
  ],
  override: true,
  defaultValue: "all",
  operator: "custom",
  filterFunction: (keyword, filterValue) => {
    if (filterValue !== "all") {
      return keyword.match_type.toLowerCase() === filterValue.toLowerCase();
    }
    return true;
  },
};

// Target Source
const getTargetSourceFilter = (channel) => {
  return {
    name: ($) => $.advertising["Source"],
    id: "target_source",
    format: "select",
    selectOptions: channel === "walmart" ? WALMART_TARGET_SOURCE_OPTIONS : AMAZON_TARGET_SOURCE_OPTIONS,
    override: true,
    defaultValue: "all",
    operator: "custom",
    filterFunction: (target, filterValue) => {
      if (filterValue !== "all") {
        return target.target_source.toLowerCase() === filterValue.toLowerCase();
      }
      return true;
    },
  };
};

// Target State
export const TARGET_STATE_FILTER = {
  name: ($) => $.advertising["State"],
  id: "target_state",
  format: "select",
  selectOptions: TARGET_STATE_OPTIONS,
  override: true,
  defaultValue: "all",
  operator: "custom",
  filterFunction: (target, filterValue) => {
    if (filterValue !== "all") {
      return target.target_state.toLowerCase() === filterValue.toLowerCase();
    }
    return true;
  },
};

// Resolves the advertising-namespace selectors carried by the filter defs above
// into plain strings, bound to the `advertising` namespace at the call site so
// the generic FilterWidget never has to resolve them. See resolveFilterOption.
export function useTargetLifecycleFilters() {
  const { t } = useTranslation(["advertising"]);
  return useMemo(
    () => ({
      BID_PREVIEW_WARNING_FILTER: resolveFilterOption(t, BID_PREVIEW_WARNING_FILTER),
      KEYWORD_MATCH_TYPE_FILTER: resolveFilterOption(t, KEYWORD_MATCH_TYPE_FILTER),
      SEARCH_TERM_REPORT_MATCH_TYPE_FILTER: resolveFilterOption(t, SEARCH_TERM_REPORT_MATCH_TYPE_FILTER),
      TARGET_MATCH_TYPE_FILTER: resolveFilterOption(t, TARGET_MATCH_TYPE_FILTER),
      TARGET_STATE_FILTER: resolveFilterOption(t, TARGET_STATE_FILTER),
      getTargetSourceFilter: (channel) => resolveFilterOption(t, getTargetSourceFilter(channel)),
      getTargetBidTypeFilter: (tableId) => resolveFilterOption(t, getTargetBidTypeFilter(tableId)),
    }),
    [t],
  );
}
